import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Alert, Spinner } from "react-bootstrap";
import { BrandButton, BrandButton2, AlertDismissable, PublishSurveyDialog } from "@/components";
import SurveyQuestionPreview from "@/components/SurveyQuestionPreview";
import SafeLocalStorage from "@/utils/safeLocalStorage";
import { useAuthenticatedApi } from "@/utils/callApi";

// Helper function: Extract row labels from expanded multi-column sub-questions
// This reverses the expansion done in SurveyBuilder
const extractRowLabels = (items, columnCount) => {
  if (!items || items.length === 0) return [];
  if (columnCount <= 1) {
    // Single column - items are already row labels
    return items.map(item => typeof item === 'string' ? item : (item.Label || item.label || ''));
  }
  
  // Multi-column: take every Nth item and extract row name (part before " - ")
  const rowLabels = [];
  for (let i = 0; i < items.length; i += columnCount) {
    const fullLabel = items[i].Label || items[i].label || '';
    const rowLabel = fullLabel.includes(' - ') ? fullLabel.split(' - ')[0] : fullLabel;
    rowLabels.push(rowLabel);
  }
  return rowLabels;
};

const formatReferenceFormula = (formula, sourceNames) => {
  if (!formula) return "";
  return formula
    .match(/\d+|[A-Za-z]+|<=|>=|[+\-*/=<>]/g)
    ?.map((token) => {
      if (/^\d+$/.test(token)) {
        const index = parseInt(token, 10);
        const isSingleDigitSource = token.length === 1 && index < sourceNames.length;
        if (isSingleDigitSource) {
          const match = sourceNames[index]?.match(/^Q\d+/);
          return match ? match[0] : token;
        }
      }
      if (/^[A-Za-z]$/.test(token)) {
        const index = token.toUpperCase().charCodeAt(0) - 65;
        if (index >= 0 && index < sourceNames.length) {
          const match = sourceNames[index]?.match(/^Q\d+/);
          return match ? match[0] : token;
        }
      }
      return token;
    })
    .join('') || formula;
};

// Helper function: Parse constraint for display
const parseConstraint = (constraint, steps, columnHeadings = [], subQuestions = []) => {
  if (constraint === "required") return "Required";
  if (constraint === "sum_equals_last_field") return "Sum of fields must equal last field (Total)";
  
  const parts = constraint.split(";");
  const type = parts[0];
  
  switch(type) {
    case "min":
      return `Minimum: ${parts[1]}`;
    case "max":
      return `Maximum: ${parts[1]}`;
    case "item_equals": {
      const itemIndex = parseInt(parts[1]);
      const questionId = parts[2];
      const referenceType = parts[3]; // 'total', 'grandtotal', or 'item-{index}'
      
      // Get the item label from subQuestions
      let itemLabel = `Item ${itemIndex + 1}`;
      if (subQuestions && subQuestions[itemIndex]) {
        itemLabel = subQuestions[itemIndex].Label || subQuestions[itemIndex].label || itemLabel;
      }
      
      let questionInfo = `Question ID ${questionId}`;
      let globalNumber = 0;
      
      if (steps && Array.isArray(steps)) {
        for (const step of steps) {
          for (const q of step.questions || []) {
            globalNumber++;
            if (q.id === parseInt(questionId)) {
              questionInfo = `${q.text.substring(0, 50)}${q.text.length > 50 ? '...' : ''} (Q${globalNumber})`;
              break;
            }
          }
          if (questionInfo !== `Question ID ${questionId}`) break;
        }
      }
      
      // Add reference type description
      let referenceDesc = "";
      if (referenceType === "grandtotal") {
        referenceDesc = " [Custom Total]";
      } else if (referenceType === "total") {
        referenceDesc = " [Total]";
      } else if (referenceType && referenceType.startsWith("item-")) {
        referenceDesc = ` [Item ${parseInt(referenceType.split("-")[1]) + 1}]`;
      }
      
      return `Item ${itemIndex + 1} (${itemLabel}) must equal ${questionInfo}${referenceDesc}`;
    }
    case "sum_equals": {
      const questionId = parts[1];
      const referenceType = parts[2]; // 'total', 'grandtotal', or 'item-{index}'
      let questionInfo = `Question ID ${questionId}`;
      let globalNumber = 0;
      
      if (steps && Array.isArray(steps)) {
        for (const step of steps) {
          for (const q of step.questions || []) {
            globalNumber++;
            if (q.id === parseInt(questionId)) {
              questionInfo = `${q.text.substring(0, 50)}${q.text.length > 50 ? '...' : ''} (Q${globalNumber})`;
              break;
            }
          }
          if (questionInfo !== `Question ID ${questionId}`) break;
        }
      }
      
      // Add reference type description
      let referenceDesc = "";
      if (referenceType === "grandtotal") {
        referenceDesc = " [Custom Total]";
      } else if (referenceType === "total") {
        referenceDesc = " [Total]";
      } else if (referenceType && referenceType.startsWith("item-")) {
        referenceDesc = ` [Item ${parseInt(referenceType.split("-")[1]) + 1}]`;
      }
      
      return `Sum must equal ${referenceDesc ? 'value from' : 'total from'} ${questionInfo}${referenceDesc}`;
    }
    case "equals_question": {
      const questionId = parts[1];
      let questionInfo = `Question ID ${questionId}`;
      let globalNumber = 0;
      
      if (steps && Array.isArray(steps)) {
        for (const step of steps) {
          for (const q of step.questions || []) {
            globalNumber++;
            if (q.id === parseInt(questionId)) {
              questionInfo = `${q.text.substring(0, 50)}${q.text.length > 50 ? '...' : ''} (Q${globalNumber})`;
              break;
            }
          }
          if (questionInfo !== `Question ID ${questionId}`) break;
        }
      }
      return `Must equal answer from ${questionInfo}`;
    }
    case "column_formula": {
      const formulaString = parts[1];
      if (!formulaString || columnHeadings.length === 0) return "Column formula (incomplete)";
      
      // Split by operators while preserving them, then replace only complete number tokens
      const formulaParts = formulaString.split(/([\+\-\=])/);
      const displayFormula = formulaParts.map(part => {
        const trimmed = part.trim();
        if (/^\d+$/.test(trimmed)) {
          const colIndex = parseInt(trimmed);
          return columnHeadings[colIndex] || `Col${colIndex}`;
        }
        return part;
      }).join(' ');
      
      return `Column formula: ${displayFormula}`;
    }
    default:
      return constraint;
  }
};

// Helper function: Parse condition for display
const parseCondition = (condition, steps) => {
  const parts = condition.split(";");
  if (parts[0] !== "if") return condition;
  
  const questionId = parts[1];
  const operator = parts[2];
  let valueType = "text";
  let value = "";
  
  if (parts.length >= 5) {
    valueType = parts[3] || "text";
    value = parts[4];
  } else if (parts.length >= 4) {
    value = parts[3];
  } else {
    return condition;
  }
  
  // Find question text
  let questionText = `Question ${questionId}`;
  for (const step of steps) {
    const q = step.questions?.find(q => q.id === parseInt(questionId));
    if (q) {
      questionText = q.text.substring(0, 30) + "...";
      break;
    }
  }
  
  const operatorText = {
    equals: "equals",
    not_equals: "does not equal",
    greater_than: "is greater than",
    less_than: "is less than"
  }[operator] || operator;
  
  // Format value display based on type
  let displayValue = value;
  if (valueType === "boolean") {
    displayValue = value === "true" ? "Yes/True" : "No/False";
  } else if (valueType === "number") {
    displayValue = `${value} (number)`;
  }
  
  return `Show if ${questionText} ${operatorText} "${displayValue}"`;
};

export default function SurveyPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { secureHelpers, isLoading, error: apiError } = useAuthenticatedApi();

  const [show, setShow] = useState(false);
  const [showSentAlert, setShowSentAlert] = useState(false);
  const [showLockedAlert, setShowLockedAlert] = useState(false);
    
    // Publish dialog state
    const [showPublishDialog, setShowPublishDialog] = useState(false);
    const [hospices, setHospices] = useState([]);
    const [selectedHospices, setSelectedHospices] = useState([]);
    const [publishToAll, setPublishToAll] = useState(true);
    const [publishStartDate, setPublishStartDate] = useState("");
    const [publishEndDate, setPublishEndDate] = useState("");
    const [autoClose, setAutoClose] = useState(false);
    
    // Survey data from API
    const [surveyData, setSurveyData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [surveyId, setSurveyId] = useState(null);

  // Always fetch survey from API so preview shows exactly what is saved
  useEffect(() => {
    const raw = typeof window !== "undefined" ? sessionStorage.getItem("previewSurvey") : null;
    let id = null;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        id = parsed?.id || null;
      } catch (e) {
        console.error("Error reading surveyId from sessionStorage:", e);
      }
    }

    if (!id) {
      setLoading(false);
      return;
    }

    setSurveyId(id);

    const fetchSurveyData = async () => {
      try {
        const survey = await secureHelpers.get(`/api/surveys/${id}`);
        const stepsData = await secureHelpers.get(`/api/steps?surveyId=${id}`);
        const questionsData = await secureHelpers.get(`/api/questions?surveyId=${id}`);

        const stepsWithQuestions = stepsData.map(step => ({
          ...step,
          questions: questionsData.filter(q => q.stepId === step.id)
        }));

        setSurveyData({
          ...survey,
          steps: stepsWithQuestions,
          questions: questionsData
        });
      } catch (error) {
        console.error('Error fetching survey preview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // support either: an array of questions, or an object { title, questions: [...] }
  const questions = useMemo(() => {
    if (!surveyData) return [];
    return surveyData.questions || [];
  }, [surveyData]);

  const title = surveyData?.title || "Survey preview";

  // tolerant answer detection (handles `answer`, `answers`, `selected`, and simple object shapes)
  const isAnswered = (q) => {
    const a = q.answer ?? q.answers ?? q.selected ?? null;
    if (q.type === "text") return String(a ?? "").trim().length > 0;
    if (q.type === "single") {
      // allow string or object with .value/.label
      return !!a && (typeof a === "string" ? a.trim().length > 0 : typeof a === "object");
    }
    if (q.type === "multi") {
      if (Array.isArray(a)) return a.length > 0;
      // some shapes might store a comma list
      if (typeof a === "string") return a.trim().length > 0;
      return false;
    }
    return false;
  };

  const completed = questions.filter(isAnswered).length

  // Load hospices for publishing
  const loadHospices = useCallback(async () => {
    try {
      const data = await secureHelpers.get('/api/hospices');
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setHospices(sortedData);
      
      // Set default dates
      const today = new Date().toISOString().split('T')[0];
      const threeMonthsAhead = new Date();
      threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3);
      const endDateDefault = threeMonthsAhead.toISOString().split('T')[0];
      
      setPublishStartDate(today);
      setPublishEndDate(endDateDefault);
      
    } catch (error) {
      console.error("Error loading hospices:", error);
      alert("Error loading hospices");
    }
  }, [secureHelpers]);

  // Open publish dialog
  const openPublishDialog = useCallback(async () => {
    if (!surveyData || !surveyData.id) {
      alert("No survey to publish. Please save the survey in the builder first.");
      return;
    }
    
    // Check if survey has roles
    if (!surveyData.roles || surveyData.roles.length === 0) {
      alert("This survey has no roles assigned. Please return to the builder and add at least one role (e.g., CEO, Finance Director) before publishing. Without roles, no members will be able to see this survey.");
      return;
    }
    
    await loadHospices();
    setShowPublishDialog(true);
  }, [surveyData, loadHospices]);

  // Navigate back to builder
  const navigateToBuilder = () => {
    navigate('/admin/surveyMaker/builder');
  };

  // Publish survey to create instances
  const publishSurvey = async () => {
    try {
      console.log("Marking survey as final and publishing...");
      
      // Use surveyId state variable which is set from surveyData.id in useEffect
      const currentSurveyId = surveyId || surveyData?.id;
      
      if (!currentSurveyId) {
        alert("Error: Survey ID not found. Please save the survey first.");
        return;
      }
      
      // First, mark the survey as final (locked)
      const updatePayload = {
        id: currentSurveyId,
        title: surveyData.title || "Untitled Survey",
        description: surveyData.description || null,
        tags: surveyData.tags || [],
        roles: surveyData.roles || [],
        heritageId: surveyData.heritageId || currentSurveyId,
        isFinal: true
      };
      
      await secureHelpers.put(`/api/surveys/${currentSurveyId}`, updatePayload);
      
      const publishData = {
        audience: publishToAll ? 0 : 1,
        audienceHospiceCodes: publishToAll ? null : selectedHospices,
        startDate: publishStartDate,
        endDate: publishEndDate,
        autoClose: autoClose
      };
      
      const result = await secureHelpers.post(`/api/surveys/${currentSurveyId}/publish`, publishData);
      
      setShowPublishDialog(false);
      
      // Reset form fields
      setPublishToAll(true);
      setSelectedHospices([]);
      setPublishStartDate("");
      setPublishEndDate("");
      setAutoClose(false);
      
      alert(`✅ ${result}\n\nThe survey is now available in the Member Data Portal!`);
      
    } catch (error) {
      console.error("Publish error:", error);
      alert(`Error publishing survey: ${error.message}`);    }
  };

  return (
    <div className="container py-4">
      <h2 style={{ color: "#F16D09", marginBottom: "1rem" }}>Survey Preview</h2>

      {loading && (
        <div className="card p-3" style={{ maxWidth: 980 }}>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading survey from API...</p>
          </div>
        </div>
      )}

      {!loading && !surveyData && (
        <div className="alert alert-warning" style={{ maxWidth: 980 }}>
          <h5>No Survey Found</h5>
          <p>Please create and save a survey in the Builder first, then click Preview.</p>
        </div>
      )}

      {surveyData && (
        <div className="card p-3" style={{ maxWidth: 980 }}>
          <div style={{ fontSize: "var(--font-lg)", fontWeight: "normal", marginBottom: "2rem" }}>
            This is how your survey will appear to hospice users. Review the steps, questions, and descriptions.
          </div>

          <hr />

          {surveyData.steps?.map((step, stepIndex) => (
          <div key={step.id} className="mb-4">
            <div style={{ marginBottom: "1rem" }}>
              <h5 style={{ color: "#F16D09", marginBottom: "0.5rem" }}>
                {step.name || step.title || `Step ${stepIndex + 1}`}
              </h5>
              
              {/* Step Description */}
              {step.description && step.description.trim() && (
                <div style={{ 
                  fontSize: "var(--font-md)",
                  color: "#6c757d",
                  marginTop: "0.5rem",
                  marginBottom: "1rem",
                  padding: "0.75rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                  borderLeft: "3px solid #F16D09"
                }}>
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Description:</span> {step.description}
                </div>
              )}
            </div>
            
            {step.questions && step.questions.length > 0 ? (
              step.questions.map((question, qIndex) => {
                // Parse question data for rendering
                let questionData = {
                  questionText: `${stepIndex + 1}.${qIndex + 1} ${question.text}`,
                  questionDescription: question.description,
                  isRequired: question.isRequired,
                  questionType: (question.type === "SingleInput" || question.type === "singleInput") 
                    ? "singleInput" 
                    : question.type === "MultipleChoice" || question.type === "multipleChoice"
                    ? "multipleChoice" 
                    : question.type === "referenceValue" || question.type === "ReferenceValue"
                    ? "referenceValue"
                    : "inputList",
                  inputType: "text",
                  subQuestions: [],
                  multipleSelectAllowed: false,
                  constraints: question.constraints || [],
                  conditions: question.conditions || [],
                  sectionHeading: "",
                  columnHeadings: [],
                  showTotal: false,
                  showGrandTotal: false,
                  grandTotalColumns: [],
                  totalFieldLabel: "Total",
                  totalFieldNote: "",
                  displayAsHeading: false,
                  displayAsInformational: false,
                  displayAsHorizontal: true
                };

                // Parse typeParameters and layout based on question type
                if (question.type === "inputList" || question.type === "InputList" || question.type === "multipleChoice" || question.type === "MultipleChoice" || question.type === "singleInput" || question.type === "SingleInput" || question.type === "referenceValue") {
                  try {
                    const typeParams = typeof question.typeParameters === 'string' ? JSON.parse(question.typeParameters || '{}') : (question.typeParameters || {});
                    questionData.inputType = typeParams.InputType || typeParams.inputType || 'text';
                    
                    if (question.type === "inputList" || question.type === "InputList") {
                      const items = question.items || typeParams.Questions || [];
                      const layoutData = typeof question.layout === 'string' ? JSON.parse(question.layout) : (question.layout || {});
                      const columnHeadings = layoutData.columnHeadings || [];
                      const columnCount = columnHeadings.length || 1;
                      const rowLabels = extractRowLabels(items, columnCount);
                      
                      questionData.subQuestions = rowLabels.map((label, idx) => ({ 
                        id: `item-${idx}`, 
                        label: label, 
                        Label: label 
                      }));
                      questionData.columnHeadings = columnHeadings;
                      questionData.showTotal = layoutData.showTotal || false;
                      questionData.showGrandTotal = layoutData.showGrandTotal || false;
                      questionData.grandTotalColumns = layoutData.grandTotalColumns || [];
                      questionData.totalFieldLabel = layoutData.totalFieldLabel || "Total";
                      questionData.totalFieldNote = layoutData.totalFieldNote || "";
                      questionData.sectionHeading = layoutData.sectionHeading || "";
                      questionData.displayAsHeading = layoutData.displayAsHeading || false;
                    } else if (question.type === "multipleChoice" || question.type === "MultipleChoice") {
                      const choices = typeParams.Choices || typeParams.choices || typeParams.Questions || typeParams.questions || [];
                      questionData.subQuestions = choices.map((choice) => ({
                        id: choice.Id ?? choice.id,
                        label: choice.Label || choice.label,
                        Label: choice.Label || choice.label
                      }));
                      questionData.multipleSelectAllowed = typeParams.MultipleSelectAllowed || typeParams.multipleSelectAllowed || false;
                      const layoutData = typeof question.layout === 'string' ? JSON.parse(question.layout) : (question.layout || {});
                      questionData.sectionHeading = layoutData.sectionHeading || "";
                      questionData.columnHeadings = layoutData.columnHeadings || [];
                      questionData.displayAsHeading = layoutData.displayAsHeading || false;
                    } else if (question.type === "singleInput") {
                      const layoutData = typeof question.layout === 'string' ? JSON.parse(question.layout) : (question.layout || {});
                      questionData.columnHeadings = layoutData.columnHeadings || [];
                      questionData.displayAsRowLabel = layoutData.displayAsRowLabel || false;
                      questionData.displayAsHeading = layoutData.displayAsHeading || false;
                      questionData.displayAsInformational = layoutData.displayAsInformational || false;
                      questionData.displayAsHorizontal = layoutData.displayAsHorizontal !== false;
                    } else if (question.type === "singleInput") {
                      const layoutData = typeof question.layout === 'string' ? JSON.parse(question.layout) : (question.layout || {});
                      questionData.columnHeadings = layoutData.columnHeadings || [];
                      questionData.displayAsRowLabel = layoutData.displayAsRowLabel || false;
                      questionData.displayAsHeading = layoutData.displayAsHeading || false;
                      questionData.displayAsInformational = layoutData.displayAsInformational || false;
                      questionData.displayAsHorizontal = layoutData.displayAsHorizontal !== false;
                    } else if (question.type === "referenceValue") {
                      // Get reference sources and formula
                      questionData.referenceSources = typeParams.ReferenceSources || [];
                      questionData.referenceFormula = typeParams.ReferenceFormula || "";
                      
                      // Build source names for display
                      questionData.referenceSourceNames = questionData.referenceSources.map((source, idx) => {
                        const questionId = source.QuestionId || source.questionId;
                        const referenceType = source.ReferenceType || source.referenceType;
                        
                        let globalNumber = 0;
                        for (const s of steps) {
                          for (const q of s.questions || []) {
                            globalNumber++;
                            if (q.id === parseInt(questionId)) {
                              const typeLabel = referenceType === 'grandtotal' ? ' (Custom Total)'
                                : referenceType === 'total' ? ' (Total)'
                                : referenceType?.startsWith('item-') ? ` (Item ${parseInt(referenceType.split('-')[1]) + 1})`
                                : '';
                              return `Q${globalNumber}: ${q.text.substring(0, 30)}${q.text.length > 30 ? '...' : ''}${typeLabel}`;
                            }
                          }
                        }
                        return `Source ${idx + 1}`;
                      });
                      
                      const layoutData = typeof question.layout === 'string' ? JSON.parse(question.layout) : (question.layout || {});
                      questionData.displayAsHeading = layoutData.displayAsHeading || false;
                    }
                  } catch (e) {
                    console.warn('Failed to parse question data:', e);
                  }
                }

                return (
                <div 
                  key={question.id} 
                  style={{ 
                    background: "#ffffff",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    padding: "1rem",
                    marginBottom: "1rem"
                  }}
                >
                  <SurveyQuestionPreview
                    questionText={questionData.questionText}
                    questionDescription={questionData.questionDescription}
                    isRequired={questionData.isRequired}
                    questionType={questionData.questionType}
                    inputType={questionData.inputType}
                    subQuestions={questionData.subQuestions}
                    multipleSelectAllowed={questionData.multipleSelectAllowed}
                    constraints={questionData.constraints}
                    conditions={questionData.conditions}
                    sectionHeading={questionData.sectionHeading}
                    columnHeadings={questionData.columnHeadings}
                    displayAsRowLabel={questionData.displayAsRowLabel}
                    displayAsHeading={questionData.displayAsHeading}
                    displayAsInformational={questionData.displayAsInformational}
                    displayAsHorizontal={questionData.displayAsHorizontal}
                    showTotal={questionData.showTotal}
                    showGrandTotal={questionData.showGrandTotal}
                    grandTotalColumns={questionData.grandTotalColumns}
                    totalFieldLabel={questionData.totalFieldLabel}
                    totalFieldNote={questionData.totalFieldNote}
                    referenceSources={questionData.referenceSources}
                    referenceSourceNames={questionData.referenceSourceNames || []}
                    referenceFormula={questionData.referenceFormula}
                    parseConstraint={parseConstraint}
                    parseCondition={parseCondition}
                  />
                  
                  {/* Display Reference Formula (for ReferenceValue questions) */}
                  {question.type === "referenceValue" && questionData.referenceSources && questionData.referenceSources.length > 0 && (
                    <div style={{ marginTop: "1rem" }}>
                      <div style={{ 
                        display: "inline-block",
                        backgroundColor: "#17a2b8",
                        color: "#ffffff",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "4px",
                        fontSize: "var(--font-sm)",
                        fontWeight: "600",
                        marginBottom: "0.5rem"
                      }}>
                        Calculated Value
                      </div>
                      <div style={{
                        backgroundColor: "#d1ecf1",
                        border: "1px solid #17a2b8",
                        borderRadius: "4px",
                        padding: "0.5rem 0.75rem",
                        fontSize: "var(--font-sm)"
                      }}>
                        Calculated from {questionData.referenceSources.length} source{questionData.referenceSources.length !== 1 ? 's' : ''}
                        {questionData.referenceFormula && questionData.referenceFormula.length > 0 && (
                          <span> using formula: <code style={{ backgroundColor: '#e9ecef', padding: '0.125rem 0.25rem', borderRadius: '2px' }}>
                            {formatReferenceFormula(questionData.referenceFormula, questionData.referenceSourceNames)}
                          </code></span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Display Constraints (Validation Rules) */}
                  {question.constraints && question.constraints.length > 0 && (
                    <div style={{ marginTop: "1rem" }}>
                      <div style={{ 
                        fontSize: "var(--font-sm)",
                        color: "#856404",
                        marginBottom: "0.5rem"
                      }}>
                        Validation Rules:
                      </div>
                      {question.constraints.map((constraint, idx) => {
                        // Get column headings for formula display
                        let columnHeadings = [];
                        try {
                          const layoutData = typeof question.layout === 'string' ? JSON.parse(question.layout) : (question.layout || {});
                          columnHeadings = layoutData.columnHeadings || [];
                        } catch (e) {
                          // Ignore parse errors
                        }
                        
                        return (
                          <div 
                            key={idx}
                            style={{
                              backgroundColor: "#fff3cd",
                              border: "1px solid #ffc107",
                              borderRadius: "4px",
                              padding: "0.5rem 0.75rem",
                              marginBottom: "0.5rem",
                              fontSize: "var(--font-sm)"
                            }}
                          >
                            {parseConstraint(constraint, surveyData.steps, columnHeadings, questionData.subQuestions)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Display Conditions */}
                  {question.conditions && question.conditions.length > 0 && (
                    <div style={{ marginTop: "1rem" }}>
                      <div style={{ 
                        fontSize: "var(--font-sm)",
                        color: "#004085",
                        marginBottom: "0.5rem"
                      }}>
                        Display Conditions:
                      </div>
                      {question.conditions.map((condition, idx) => (
                        <div 
                          key={idx}
                          style={{
                            backgroundColor: "#d1ecf1",
                            border: "1px solid #17a2b8",
                            borderRadius: "4px",
                            padding: "0.5rem 0.75rem",
                            marginBottom: "0.5rem",
                            fontSize: "var(--font-sm)"
                          }}
                        >
                          {parseCondition(condition, surveyData.steps)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                );
              })
            ) : (
              <p className="text-muted">No questions in this step</p>
            )}
          </div>
        ))}

        <Alert variant="info" className="mt-4">
          <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Preview Mode:</span> This is how your survey will appear to hospice users. Questions cannot be answered in preview mode.
        </Alert>
      </div>
      )}

      {/* Action Buttons */}
      {surveyData && (
        <div className="d-flex gap-2 mt-4">
        <BrandButton
          onClick={navigateToBuilder}
          
        >
          ← Back to Survey Builder
        </BrandButton>

        <BrandButton
          onClick={openPublishDialog}
          
        >
          Publish Survey to Hospices
        </BrandButton>
      </div>
      )}

      {/* Publish Dialog - Modal */}
      <PublishSurveyDialog
        show={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        surveyTitle={title}
        onPublish={publishSurvey}
        hospices={hospices}
        selectedHospices={selectedHospices}
        setSelectedHospices={setSelectedHospices}
        publishToAll={publishToAll}
        setPublishToAll={setPublishToAll}
        startDate={publishStartDate}
        setStartDate={setPublishStartDate}
        endDate={publishEndDate}
        setEndDate={setPublishEndDate}
        autoClose={autoClose}
        setAutoClose={setAutoClose}
      />
    </div>
  );
}
